import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import QueryString from "query-string";

import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import Typography from "@material-ui/core/Typography";
import Pagination from "@material-ui/lab/Pagination";
import { makeStyles } from "@material-ui/core/styles";

import EditIcon from "@material-ui/icons/Edit";

import history from "../../store/history";
import { FEEDBACK_GET_LIST_URL, COLOR_GREEN, COLOR_BLUE, COLOR_BROWN, COLOR_GRAY, RECORDS_FETCH_LIMIT } from "../../config";
import makeApiRequest from "../../utils/axios/new-axios";
import { showErrorAlert } from "../../store/actions/alerts";
import ComponentLoader from "../../components/loader/ComponentLoader";
import { usePageTitle } from "../../utils/pageTitle";
import UserChip from "../../components/user/UserChip";
import { fromUnixtime } from "../../utils/time";
import TextField from "../../components/formik/TextField";
import SelectPlain from "../../components/formik/SelectPlain";
import SubmitButton from "../../components/button/SubmitButton";
import { permissions, types, checkAcl } from "../../utils/acl";
import FeedbackRespondForm from "../../components/feedback/FeedbackRespondForm";

const useStyles = makeStyles(theme => ({
    paper: {
        width: "100%",
        overflowX: "auto",
        marginBottom: theme.spacing(2),
        padding: "15px"
    },
    form: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        paddingBottom: "13px",
        minWidth: "300px",
    },
    select: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        paddingBottom: "13px",
        minWidth: "130px",
    }
}));

export default function SearchFeedback({ location }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    usePageTitle("Feedback");
    const EDIT_ACCESS = checkAcl({ p: permissions.TRAVALOUR_USER_MANAGEMENT, t: types.VIEW_AND_EDIT });

    const [result, setResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({
        text: "",
        status: -1,
        sortBy: 3,
    });

    // Respond Modal
    const [openRespondModal, setOpenRespondModal] = useState(false);
    const [respondParams, setRespondParams] = useState({
        id: 0,
        status: -1,
        comment: ""
    });

    const pathName = location.pathname;

    useEffect(() => {

        const query = QueryString.parse(location.search);

        if (!!query) {
            const text = !!query.text ? query.text.trim() : null;
            const status = query.status && Number(query.status) >= 0 ? Number(query.status) : null;
            const page = Number(query.page) || undefined;
            const limit = Number(query.limit) || RECORDS_FETCH_LIMIT;

            const sort = !!query.sort ? query.sort.trim().toLowerCase() : null;
            const order = !!query.order ? query.order.trim().toLowerCase() : null;
            const sortBy =
                sort === "name"
                    ? order === "desc"
                        ? 2
                        : 1
                    : sort === "date"
                        ? order === "desc"
                            ? 3
                            : 4
                        : 3;

            setInitialValues({
                text: text || "",
                status: status !== null ? status : -1,
                sortBy,
            });

            setIsLoading(true);

            makeApiRequest({
                url: FEEDBACK_GET_LIST_URL.url,
                method: FEEDBACK_GET_LIST_URL.method,
                params: {
                    text,
                    status,
                    sort,
                    order,
                    page,
                    limit
                }
            }).then(response => {
                setResult(response.data);
                setIsLoading(false);
            }).catch((error) => {
                setIsLoading(false);
                dispatch(
                    showErrorAlert(
                        "Fetch Error : " +
                        (!!error.response
                            ? `${error.response.status}! - ${error.response.data.errorMessage}`
                            : error.message)
                    )
                );

            })
        }
        // eslint-disable-next-line
    }, []); // [] is required to make sure that it runs only once

    const handlePagination = (e, page) => {
        const query = QueryString.parse(location.search);
        history.push(
            `${pathName}?${QueryString.stringify({
                ...query,
                page,
            })}`
        );
    };



    function handleOpenRespondModal() {
        setOpenRespondModal(true);
    }

    function handleCloseRespondModal() {
        setOpenRespondModal(false);
    }


    const editButtonClick = (id, status, comment) => {
        setRespondParams({
            id,
            status,
            comment: !!comment ? comment : ""
        });
        handleOpenRespondModal();
    };

    return (
        <Container style={{ marginTop: "20px", padding: "10px" }}>
            <h3>Public User Feedback </h3>
            <Paper className={classes.paper}>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={(values) => {
                        let sort = "name";
                        let order = "asc";
                        switch (values.sortBy) {
                            case 1: //no changes
                                break;
                            case 2:
                                order = "desc";
                                break;
                            case 3:
                                sort = "date";
                                order = "desc";
                                break;
                            case 4:
                                sort = "date";
                                break;
                            default:
                                //no changes
                                break;
                        }

                        const params = {
                            text: values.text.trim().length > 0 ? values.text.trim() : null,
                            status: values.status >= 0 ? values.status : null,
                            sort,
                            order,
                            page: 1,
                            limit: RECORDS_FETCH_LIMIT,
                        };

                        //remove the null keys
                        Object.keys(params).forEach(
                            (key) => params[key] == null && delete params[key]
                        );

                        history.push(`${pathName}?${QueryString.stringify(params)}`);
                    }}
                >
                    {(props) => {
                        const { touched, errors } = props;
                        return (
                            <Form className={classes.form}>
                                <Field
                                    component={TextField}
                                    name="text"
                                    margin="dense"
                                    label="Search Text"
                                    className={classes.textField}
                                    helperText={touched.name && errors.name && errors.name}
                                    error={!!touched.name && !!errors.name}
                                    disabled={isLoading}
                                />
                                <Field
                                    component={SelectPlain}
                                    name="status"
                                    label="Status"
                                    disabled={isLoading}
                                    className={classes.select}
                                    options={[
                                        {
                                            value: -1,
                                            label: "All",
                                        },
                                        {
                                            value: 0,
                                            label: "New",
                                        },
                                        {
                                            value: 1,
                                            label: "Responded",
                                        },
                                        {
                                            value: 2,
                                            label: "Spam",
                                        },
                                        {
                                            value: 3,
                                            label: "Other",
                                        },
                                    ]}
                                />
                                <Field
                                    component={SelectPlain}
                                    name="sortBy"
                                    label="Sort By"
                                    disabled={isLoading}
                                    className={classes.select}
                                    options={[
                                        {
                                            value: 1,
                                            label: "Name: Ascending",
                                        },
                                        {
                                            value: 2,
                                            label: "Name: Descending",
                                        },
                                        {
                                            value: 3,
                                            label: "Date: Newest",
                                        },
                                        {
                                            value: 4,
                                            label: "Date: Oldest",
                                        },
                                    ]}
                                />
                                <SubmitButton disabled={isLoading}>Search</SubmitButton>
                            </Form>
                        );
                    }}
                </Formik>
            </Paper>
            <ComponentLoader isLoading={isLoading} />

            {!isLoading && result && result.feedback &&
                <>
                    {result.feedback.length > 0 ? (
                        <Paper className={classes.paper}>
                            <Table className={classes.table} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Feedback ID</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Feedback</TableCell>
                                        <TableCell>Reference</TableCell>
                                        <TableCell>Created On</TableCell>
                                        <TableCell>Responded By</TableCell>
                                        <TableCell>Responded On</TableCell>
                                        <TableCell>Responder Comments</TableCell>
                                        <TableCell>Status</TableCell>
                                        {EDIT_ACCESS && <TableCell>Action</TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {result.feedback.map(f => {
                                        return (
                                            <TableRow key={f.id} style={{ verticalAlign: 'top' }}>
                                                <TableCell>{f.id}</TableCell>
                                                <TableCell>{f.name}</TableCell>
                                                <TableCell>{f.email}</TableCell>
                                                <TableCell>
                                                    <Typography style={{ whiteSpace: "pre-line" }}>{f.feedback.replace(/\n\s*\n/g, '\n')}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography style={{ whiteSpace: "pre-line" }}>{f.reference}</Typography>
                                                </TableCell>
                                                <TableCell>{fromUnixtime(f.createdOn)}</TableCell>
                                                <TableCell>{f.respondedBy &&
                                                    <UserChip
                                                        user={{
                                                            id: f.respondedBy.id,
                                                            name: f.respondedBy.name,
                                                            username: f.respondedBy.username
                                                        }}
                                                    />
                                                }</TableCell>
                                                <TableCell>{f.respondedOn && fromUnixtime(f.respondedOn)}</TableCell>
                                                <TableCell>{f.responderComments}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={f.status === 0 ? "New" : f.status === 1 ? "Responded" : f.status === 2 ? "Spam" : "Other"}
                                                        className={classes.chip}
                                                        style={{ backgroundColor: f.status === 0 ? COLOR_GREEN : f.status === 1 ? COLOR_BLUE : f.status === 2 ? COLOR_BROWN : COLOR_GRAY }}
                                                    />
                                                </TableCell>
                                                {EDIT_ACCESS &&
                                                    <TableCell>
                                                        <IconButton
                                                            className={classes.button}
                                                            aria-label="edit-correction"
                                                            onClick={() => editButtonClick(f.id, f.status, f.responderComments)}
                                                            size="small"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                }
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>

                            </Table>

                        </Paper>
                    ) : (
                        <Typography align="center">No requests found !!</Typography>
                    )}

                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ margin: "15px" }}
                        display="block"
                        gutterBottom
                        align="center"
                    >
                        {result.stats &&
                            `Total Result(s) : ${result.stats.records.total}, Fetched : ${result.stats.records.fetched}, Page : ${result.stats.pages.current}`}
                    </Typography>
                    {result.stats && result.stats.pages &&
                        <Box
                            justifyContent="center"
                            display="flex"
                            style={{ margin: "20px" }}
                        >
                            <Pagination
                                count={result.stats.pages.total}
                                page={result.stats.pages.current}
                                boundaryCount={3}
                                color="primary"
                                shape="rounded"
                                variant="outlined"
                                onChange={handlePagination}
                            />
                        </Box>
                    }
                </>
            }
            {EDIT_ACCESS &&
                <FeedbackRespondForm
                    openModal={openRespondModal}
                    handleCloseModal={handleCloseRespondModal}
                    respondParams={respondParams}
                />
            }
        </Container>
    )
}
